/* Choosing a password, from the link Damga or the shop's owner sent.
 *
 * THE ONLY PLACE A MERCHANT PASSWORD IS EVER SET, and Damga never sees it
 * (ruled 2026-09-18). The link's token is read from the URL fragment by the app,
 * which never reaches a server, and the app clears it from the address bar before
 * this renders. The username is shown here, from Doha, and never in the email:
 * a shop may have made it from its own name, and that never travels through the
 * mail provider.
 *
 * A DEAD LINK SAYS SO AND SAYS WHAT TO DO. Used, expired or never real are one
 * state, and the person is sent to whoever can issue another.
 */
import type { MerchantAuth } from "@damga/kit";
import { type FormEvent, type ReactNode, useEffect, useState } from "react";

export type MerchantSetupWords = {
  checking: string;
  title: string;
  /** "{username}" is replaced. */
  body: string;
  username: string;
  newPassword: string;
  confirmPassword: string;
  rule: string;
  mismatch: string;
  /** "{reason}" is replaced with the server's words, e.g. "at least 12 characters". */
  weak: string;
  submit: string;
  working: string;
  doneTitle: string;
  doneBody: string;
  signIn: string;
  deadTitle: string;
  deadBody: string;
  unreachable: string;
};

export function MerchantSetup({
  auth,
  token,
  words,
  brand,
  onDone,
}: {
  auth: MerchantAuth;
  token: string;
  words: MerchantSetupWords;
  brand?: ReactNode;
  onDone: (username: string) => void;
}) {
  const [state, setState] = useState<"checking" | "form" | "done" | "dead">("checking");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [again, setAgain] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const out = await auth.checkSetup(token);
        if (!live) return;
        if (out.kind === "ok") {
          setUsername(out.username);
          setState("form");
        } else setState("dead");
      } catch {
        if (live) {
          setProblem(words.unreachable);
          setState("dead");
        }
      }
    })();
    return () => {
      live = false;
    };
  }, [auth, token, words.unreachable]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    if (!password || password !== again) {
      setProblem(words.mismatch);
      return;
    }
    setBusy(true);
    setProblem(null);
    try {
      const out = await auth.setPassword(token, password);
      if (out.kind === "ok") {
        setPassword("");
        setAgain("");
        setUsername(out.username);
        setState("done");
      } else if (out.kind === "weak") {
        setProblem(words.weak.replace("{reason}", out.reason));
      } else {
        setState("dead");
      }
    } catch {
      setProblem(words.unreachable);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="msi">
      <div className="msi-card">
        {brand}
        {state === "checking" && <p className="msi-body">{words.checking}</p>}

        {state === "dead" && (
          <div className="msi-form">
            <h1 className="msi-title">{words.deadTitle}</h1>
            <p className="msi-body">{problem ?? words.deadBody}</p>
          </div>
        )}

        {state === "done" && (
          <div className="msi-form">
            <h1 className="msi-title">{words.doneTitle}</h1>
            <p className="msi-body">{words.doneBody.replace("{username}", username)}</p>
            <button className="msi-submit" type="button" onClick={() => onDone(username)}>
              {words.signIn}
            </button>
          </div>
        )}

        {state === "form" && (
          <form className="msi-form" onSubmit={submit} noValidate>
            <h1 className="msi-title">{words.title}</h1>
            <p className="msi-body">{words.body.replace("{username}", username)}</p>
            {/* For the browser's password manager: it pairs the new password
                with this username rather than guessing one. */}
            <label className="msi-field">
              <span className="msi-label">{words.username}</span>
              <input className="msi-input" name="username" autoComplete="username" value={username} readOnly />
            </label>
            <label className="msi-field">
              <span className="msi-label">{words.newPassword}</span>
              <input
                className="msi-input"
                name="new-password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                aria-describedby="msi-rule"
              />
            </label>
            <p className="msi-body msi-rule" id="msi-rule">
              {words.rule}
            </p>
            <label className="msi-field">
              <span className="msi-label">{words.confirmPassword}</span>
              <input
                className="msi-input"
                name="confirm-password"
                type="password"
                autoComplete="new-password"
                value={again}
                onChange={(e) => setAgain(e.target.value)}
              />
            </label>
            {problem && (
              <p className="msi-problem" role="alert">
                {problem}
              </p>
            )}
            <button className="msi-submit" type="submit" aria-busy={busy}>
              {busy ? words.working : words.submit}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

/** The setup token from `#setup=<token>`, once, and the address bar cleared so it
 *  is not left in history. At the root, so the link opens on any host with no
 *  rewrite rule behind it. */
export function takeSetupToken(): string | null {
  if (typeof window === "undefined") return null;
  const match = /^#setup=([A-Za-z0-9_-]{20,200})$/.exec(window.location.hash);
  if (!match) return null;
  try {
    window.history.replaceState(null, "", window.location.pathname);
  } catch {
    /* a sandboxed frame; the token still leaves with this tab */
  }
  return match[1] ?? null;
}
